import { Component, computed } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  DynamicFormField,
  formBuilder,
  input,
  number,
  textarea,
} from '@kinet-open-network/client/core/forms/ui';
import { IAutomationActionAiPromptData } from '@kinet-open-network/shared/data-access/model';
import { FormlyModule } from '@ngx-formly/core';
import {
  ACTION_DATA_TEMPLATE,
  ActionComponent,
} from './abstract-action.component';

const isValidJsonObject = (control: AbstractControl) => {
  const value = control.value;

  if (!value?.trim()) {
    return true;
  }

  try {
    const parsed = JSON.parse(value);
    return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed);
  } catch {
    return false;
  }
};

@Component({
  selector: 'kon-ai-prompt-action',
  imports: [FormlyModule],
  template: ACTION_DATA_TEMPLATE,
})
export class AiPromptActionComponent extends ActionComponent<'ai_prompt'> {
  override fields = computed<DynamicFormField[]>(() =>
    formBuilder([
      input('modelProfile', 'Profil modelu').required(true),
      textarea(
        'systemPrompt',
        'Systémový prompt (statická časť, napr. {{stockItemsCatalog}})',
      ).props({ rows: 8 }),
      textarea(
        'instructions',
        'Inštrukcie (dáta kontextu napr. {{message.text}}, {{customer.name}}, {{order.total}})',
      )
        .required(true)
        .props({ rows: 8 }),
      textarea('outputSchemaText', 'Schéma výstupu (JSON schema)')
        .props({ rows: 12 })
        .validators({
          json: {
            expression: isValidJsonObject,
            message: 'Neplatný JSON objekt',
          },
        }),
      number('maxOutputTokens', 'Max. výstupných tokenov', 1, 100000),
      number('timeoutSeconds', 'Timeout (s)', 1, 600),
    ]),
  );

  protected override dataToModel(
    data: Partial<IAutomationActionAiPromptData>,
  ): Record<string, unknown> {
    const { outputSchema, ...rest } = data;

    return {
      ...rest,
      outputSchemaText: outputSchema
        ? JSON.stringify(outputSchema, null, 2)
        : '',
    };
  }

  protected override modelToData(
    model: Record<string, unknown>,
  ): Partial<IAutomationActionAiPromptData> {
    const { outputSchemaText, ...rest } = model;
    let outputSchema: Record<string, unknown> | undefined;

    try {
      outputSchema =
        typeof outputSchemaText === 'string' && outputSchemaText.trim()
          ? JSON.parse(outputSchemaText)
          : undefined;
    } catch {
      // Invalid JSON is flagged by the field validator and keeps the form invalid.
      outputSchema = undefined;
    }

    return { ...rest, outputSchema } as Partial<IAutomationActionAiPromptData>;
  }
}
